import * as React from "react";
import { Button, Field, Input, Text, makeStyles } from "@fluentui/react-components";
import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import {
  DATAVERSE_URL_PREFIX,
  DATAVERSE_URL_SUFFIX,
  DEFAULT_DATAVERSE_HOST,
  getDataverseApiBaseUrlFromHostSegment,
  getDataverseDelegatedScopeFromHostSegment,
  normalizeDataverseHostSegment,
} from "../services/dataverse/url";
import type { DataverseConnectionConfig } from "../services/dataverse/types";

const useStyles = makeStyles({
  card: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "10px",
    backgroundColor: "var(--colorNeutralBackground1)",
    padding: "16px",
    display: "grid",
    gap: "12px",
  },
  authTopRow: {
    display: "flex",
    gap: "16px",
    alignItems: "end",
    flexWrap: "wrap",
  },
  dataverseField: {
    width: "500px",
    maxWidth: "100%",
  },
  authPanel: {
    display: "grid",
    gap: "8px",
    minWidth: "320px",
    flex: 1,
  },
  buttonRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  errorText: {
    color: "var(--colorPaletteRedForeground1)",
  },
  successText: {
    color: "var(--colorPaletteGreenForeground1)",
  },
});

const LEGACY_BASE_URL_KEY = "fetchxml-builder:base-url";
const DATAVERSE_HOST_KEY = "fetchxml-builder:dataverse-host";
const TOKEN_KEY = "fetchxml-builder:token";

const DEFAULT_AUTH_TENANT_ID = "organizations";

export interface DataverseLoginControlProps {
  connection: DataverseConnectionConfig | null;
  onConnectionChange: (connection: DataverseConnectionConfig | null) => void;
  onDisconnected?: () => void;
}

function getAppRedirectUri(): string {
  return window.location.origin;
}

function getAuthority(tenantId: string): string {
  return `https://login.microsoftonline.com/${tenantId}`;
}

const runtimeAuthConfig = window.__FETCHXML_RUNTIME_CONFIG__;
const AUTH_CLIENT_ID =
  runtimeAuthConfig?.DATAVERSE_AUTH_CLIENT_ID?.trim() ||
  process.env.REACT_APP_DATAVERSE_AUTH_CLIENT_ID?.trim() ||
  "";
const AUTH_TENANT_ID =
  runtimeAuthConfig?.DATAVERSE_AUTH_TENANT_ID?.trim() ||
  process.env.REACT_APP_DATAVERSE_AUTH_TENANT_ID?.trim() ||
  DEFAULT_AUTH_TENANT_ID;
const AUTH_AUTHORITY =
  runtimeAuthConfig?.DATAVERSE_AUTH_AUTHORITY?.trim() ||
  process.env.REACT_APP_DATAVERSE_AUTH_AUTHORITY?.trim() ||
  getAuthority(AUTH_TENANT_ID);
const AUTH_IS_CONFIGURED = Boolean(AUTH_CLIENT_ID);

function getAuthScopes(dataverseScope: string): string[] {
  return ["openid", "profile", "offline_access", dataverseScope];
}

function getInitialDataverseHost(): string {
  const savedHost = localStorage.getItem(DATAVERSE_HOST_KEY);
  if (savedHost) {
    return savedHost;
  }

  const legacyBaseUrl = localStorage.getItem(LEGACY_BASE_URL_KEY);
  if (legacyBaseUrl) {
    try {
      const hostname = new URL(legacyBaseUrl).hostname;
      if (hostname.endsWith(".dynamics.com")) {
        return hostname.slice(0, -".dynamics.com".length);
      }
    } catch (_error) {
      // Ignore malformed legacy values and fall through to default.
    }
  }

  return DEFAULT_DATAVERSE_HOST;
}

export function DataverseLoginControl({ connection, onConnectionChange, onDisconnected }: DataverseLoginControlProps): JSX.Element {
  const styles = useStyles();
  const [dataverseHost, setDataverseHost] = React.useState(getInitialDataverseHost);
  const [authError, setAuthError] = React.useState("");
  const [authInfo, setAuthInfo] = React.useState("");
  const [authLoading, setAuthLoading] = React.useState(false);
  const msalClientRef = React.useRef<PublicClientApplication | null>(null);
  const msalClientSignatureRef = React.useRef("");

  const clearConnection = React.useCallback((message = "Disconnected.") => {
    onConnectionChange(null);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LEGACY_BASE_URL_KEY);
    setAuthError("");
    setAuthInfo(message);
    onDisconnected?.();
  }, [onConnectionChange, onDisconnected]);

  const getMsalClient = React.useCallback(async (): Promise<PublicClientApplication> => {
    if (!AUTH_IS_CONFIGURED) {
      throw new Error("Missing Microsoft Entra client ID. Set DATAVERSE_AUTH_CLIENT_ID in Docker or REACT_APP_DATAVERSE_AUTH_CLIENT_ID at build time.");
    }

    const signature = `${AUTH_CLIENT_ID}|${AUTH_AUTHORITY}`;

    if (!msalClientRef.current || msalClientSignatureRef.current !== signature) {
      msalClientRef.current = new PublicClientApplication({
        auth: {
          clientId: AUTH_CLIENT_ID,
          authority: AUTH_AUTHORITY,
          redirectUri: getAppRedirectUri(),
          postLogoutRedirectUri: getAppRedirectUri(),
        },
        cache: {
          cacheLocation: "sessionStorage",
        },
      });
      msalClientSignatureRef.current = signature;
      await msalClientRef.current.initialize();
    }

    return msalClientRef.current;
  }, []);

  const onDisconnect = React.useCallback(async () => {
    setAuthError("");
    setAuthInfo("");

    try {
      setAuthLoading(true);
      const msal = await getMsalClient();
      const account = msal.getAllAccounts()[0];

      clearConnection("Signing out...");

      if (account) {
        await msal.logoutRedirect({
          account,
          postLogoutRedirectUri: getAppRedirectUri(),
        });
        return;
      }

      clearConnection("Signed out from this tab.");
    } catch (error) {
      setAuthError(`Logout failed: ${String(error)}`);
    } finally {
      setAuthLoading(false);
    }
  }, [clearConnection, getMsalClient]);

  const onSwitchAccount = React.useCallback(async () => {
    if (!AUTH_IS_CONFIGURED) {
      setAuthError("Missing Microsoft Entra client ID. Configure DATAVERSE_AUTH_CLIENT_ID.");
      return;
    }

    const normalizedHost = normalizeDataverseHostSegment(dataverseHost);

    setAuthError("");
    setAuthInfo("");

    if (!normalizedHost) {
      setAuthError("Please provide Dataverse host part first.");
      return;
    }

    let dataverseScope: string;
    try {
      dataverseScope = getDataverseDelegatedScopeFromHostSegment(normalizedHost);
    } catch (_error) {
      setAuthError("Dataverse host part is invalid. Use a value such as kodikas-dev.crm4.");
      return;
    }

    try {
      setAuthLoading(true);
      localStorage.setItem(DATAVERSE_HOST_KEY, normalizedHost);

      clearConnection("Switching account...");

      const msal = await getMsalClient();
      await msal.loginRedirect({
        scopes: getAuthScopes(dataverseScope),
        redirectUri: getAppRedirectUri(),
        prompt: "select_account",
      });
    } catch (error) {
      setAuthError(String(error));
    } finally {
      setAuthLoading(false);
    }
  }, [clearConnection, dataverseHost, getMsalClient]);

  const applyAuthResult = React.useCallback(
    (accessToken: string, signedInUser?: string): void => {
      const normalizedHost = normalizeDataverseHostSegment(dataverseHost);
      const normalizedBaseUrl = getDataverseApiBaseUrlFromHostSegment(normalizedHost);

      onConnectionChange({
        baseUrl: normalizedBaseUrl,
        bearerToken: accessToken,
      });

      localStorage.setItem(DATAVERSE_HOST_KEY, normalizedHost);
      localStorage.setItem(LEGACY_BASE_URL_KEY, normalizedBaseUrl);
      sessionStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.removeItem(TOKEN_KEY);

      setAuthError("");
      setAuthInfo(`Signed in as ${signedInUser || "Microsoft account"}.`);
    },
    [dataverseHost, onConnectionChange],
  );

  React.useEffect(() => {
    if (connection) {
      return;
    }

    const savedToken = sessionStorage.getItem(TOKEN_KEY)?.trim() || localStorage.getItem(TOKEN_KEY)?.trim();
    if (!savedToken) {
      return;
    }

    if (!sessionStorage.getItem(TOKEN_KEY) && localStorage.getItem(TOKEN_KEY)) {
      sessionStorage.setItem(TOKEN_KEY, savedToken);
      localStorage.removeItem(TOKEN_KEY);
    }

    try {
      const normalizedBaseUrl = getDataverseApiBaseUrlFromHostSegment(dataverseHost);
      onConnectionChange({
        baseUrl: normalizedBaseUrl,
        bearerToken: savedToken,
      });
      setAuthInfo((current) => current || "Connected using saved token.");
    } catch (_error) {
      // Ignore malformed persisted values.
    }
  }, [connection, dataverseHost, onConnectionChange]);

  React.useEffect(() => {
    let disposed = false;

    const completeRedirectSignIn = async (): Promise<void> => {
      try {
        const msal = await getMsalClient();
        const redirectResult = await msal.handleRedirectPromise();
        if (disposed) {
          return;
        }

        if (redirectResult?.accessToken) {
          applyAuthResult(redirectResult.accessToken, redirectResult.account?.username);
          return;
        }

        if (!dataverseHost.trim()) {
          return;
        }

        let dataverseScope: string;
        try {
          dataverseScope = getDataverseDelegatedScopeFromHostSegment(dataverseHost);
        } catch (_error) {
          return;
        }

        const account = redirectResult?.account || msal.getAllAccounts()[0];
        if (!account) {
          return;
        }

        const silentResult = await msal.acquireTokenSilent({
          scopes: getAuthScopes(dataverseScope),
          account,
        });
        if (disposed) {
          return;
        }

        applyAuthResult(silentResult.accessToken, silentResult.account?.username);
      } catch (error) {
        if (disposed) {
          return;
        }

        setAuthError(String(error));
      }
    };

    void completeRedirectSignIn();

    return () => {
      disposed = true;
    };
  }, [applyAuthResult, dataverseHost, getMsalClient]);

  const onMicrosoftSignIn = React.useCallback(async () => {
    if (!AUTH_IS_CONFIGURED) {
      setAuthError("Missing Microsoft Entra client ID. Configure DATAVERSE_AUTH_CLIENT_ID.");
      return;
    }

    const normalizedHost = normalizeDataverseHostSegment(dataverseHost);

    setAuthError("");
    setAuthInfo("");

    if (!normalizedHost) {
      setAuthError("Please provide Dataverse host part first.");
      return;
    }

    let dataverseScope: string;
    try {
      dataverseScope = getDataverseDelegatedScopeFromHostSegment(normalizedHost);
    } catch (_error) {
      setAuthError("Dataverse host part is invalid. Use a value such as kodikas-dev.crm4.");
      return;
    }

    try {
      setAuthLoading(true);
      localStorage.setItem(DATAVERSE_HOST_KEY, normalizedHost);

      const msal = await getMsalClient();
      const account = msal.getAllAccounts()[0];

      try {
        if (account) {
          const silentResult = await msal.acquireTokenSilent({
            scopes: getAuthScopes(dataverseScope),
            account,
          });
          applyAuthResult(silentResult.accessToken, silentResult.account?.username);
          return;
        }
      } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) {
          throw error;
        }
      }

      await msal.loginRedirect({
        scopes: getAuthScopes(dataverseScope),
        redirectUri: getAppRedirectUri(),
        prompt: "select_account",
      });
    } catch (error) {
      setAuthError(String(error));
    } finally {
      setAuthLoading(false);
    }
  }, [applyAuthResult, dataverseHost, getMsalClient]);

  return (
    <div className={styles.card}>
      <Text size={600} weight="semibold">
        FetchXML Builder
      </Text>
      <Text>Sign in interactively with Microsoft Entra.</Text>
      {!AUTH_IS_CONFIGURED ? (
        <Text className={styles.errorText}>
          Microsoft Entra client ID is not configured. Set DATAVERSE_AUTH_CLIENT_ID in Docker or REACT_APP_DATAVERSE_AUTH_CLIENT_ID at build time.
        </Text>
      ) : null}
      <div className={styles.authTopRow}>
        <Field className={styles.dataverseField} label="Dataverse URL">
          <Input
            value={dataverseHost}
            onChange={(_, data) => setDataverseHost(data.value)}
            contentBefore={DATAVERSE_URL_PREFIX}
            contentAfter={DATAVERSE_URL_SUFFIX}
            placeholder="kodikas-dev.crm4"
          />
        </Field>
        <div className={styles.authPanel}>
          <div className={styles.buttonRow}>
            <Button appearance="primary" onClick={onMicrosoftSignIn} disabled={authLoading || !AUTH_IS_CONFIGURED || !dataverseHost.trim() || Boolean(connection)}>
              {authLoading ? "Preparing sign-in..." : "Sign in with Microsoft"}
            </Button>
            <Button appearance="secondary" onClick={() => void onSwitchAccount()} disabled={authLoading || !AUTH_IS_CONFIGURED || !dataverseHost.trim()}>
              Switch account
            </Button>
            <Button appearance="secondary" onClick={() => void onDisconnect()} disabled={!connection || authLoading}>
              Log out
            </Button>
            <Text>{connection ? "Connected" : "Not connected"}</Text>
          </div>
          {authError ? <Text className={styles.errorText}>{authError}</Text> : null}
          {authInfo ? <Text className={styles.successText}>{authInfo}</Text> : null}
        </div>
      </div>
    </div>
  );
}
